// @ts-nocheck
/**
 * Agent Leads Admin — redesigned to obd3-gui pattern (ScrollPage + PageHeader
 * + stats Cards + Charts + shadcn table + DropdownMenu row actions +
 * ConfirmDialog for delete, shadcn Dialogs for create/transfer).
 * Backend contract preserved: create/list/delete/transfer endpoints and
 * exact payload shape (assignment_type: agent | call_group | campaign).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Chart from 'react-apexcharts';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import {
  ArrowLeftRight,
  ArrowRight,
  CheckCheck,
  Download,
  Eye,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  RotateCcw,
  Settings2,
  Trash2,
  Users,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { MAX_LEAD_CONTACTS, parseLeadPaste, type LeadPasteResult } from './leadContacts';
import { EditLeadTitleDialog, LEAD_TITLE_MAX, leadTitleError, transferMovedLabel } from './leadAssignmentUi';
import { LeadsPager, usePagedLeads } from './usePagedLeads';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { AclDenied } from '@/components/feedback/AclDenied';
import { currentUserCanUseLeads } from '@/utilities/callCentreLeadsAcl';
import { canCreateAgentLeads, canMutateAgentLeads } from '@/utilities/agentLeadsQuota';
import { getUser } from '@/utilities/setting';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import { cn } from '@/lib/utils/cn';
import Loader from '@/components/partials/components/Loader';
import { getApiCall, postApiCall } from '@/service/httpCall';
import {
  API_AGENT_LEADS_LIST,
  API_AGENT_LEADS_BALANCE,
  API_AGENT_LEADS_CREATE,
  API_AGENT_LEADS_DELETE,
  API_AGENT_LEADS_TRANSFER,
  API_AGENT_LEADS_TRANSFER_RECIPIENTS,
  API_AGENT_LEADS_ITEMS,
  API_AGENT_LEADS_MARK_COMPLETE,
  API_CONTACT_GROUP_LIST,
  API_DISPOSITION_LIST,
  API_AGENTS_LIST,
  API_AGENT_GROUPS_LIST,
  API_VOICE_CAMPAIGNS_LIST,
} from '@/service/url';
import { ROUTES } from '@/utilities/constant';
import { exportToCSV } from '@/utilities/helpers';
import { formMatchesCallDirection, formatDispositionCallDirectionLabel } from '@/utilities/dispositionCallDirection';
import { CalendarDateInput } from '@/components/ui/calendar-inputs';

const toLocalDateStr = (d: Date) => {
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10);
};
const TODAY_STR = toLocalDateStr(new Date());
const FIRST_OF_MONTH_STR = (() => {
  const d = new Date();
  return toLocalDateStr(new Date(d.getFullYear(), d.getMonth(), 1));
})();
const MIN_DATE_STR = (() => {
  const d = new Date();
  return toLocalDateStr(new Date(d.getFullYear(), d.getMonth() - 3, d.getDate()));
})();
const isWithinThreeMonths = (from: string, to: string) => {
  if (!from || !to) return true;
  const limit = new Date(`${from}T00:00:00`);
  limit.setMonth(limit.getMonth() + 3);
  return new Date(`${to}T00:00:00`) <= limit;
};

const CAMPAIGN_TYPE_LABEL: any = {
  1: 'Click2Call',
  2: 'Auto',
  3: 'Predictive',
  4: 'Power',
};

const StatusBadge = ({ status }: any) => (
  <span
    className={
      status === 'completed'
        ? 'inline-flex rounded-md bg-emerald-500/15 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-400'
        : 'inline-flex rounded-md bg-amber-500/15 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-400'
    }
  >
    {status === 'completed' ? 'Completed' : 'Pending'}
  </span>
);

const transferCounts = (row: any) => {
  const total = Number(row?.total_leads) || 0;
  const completed = Math.min(Number(row?.completed_leads) || 0, total);
  return { total, completed, pending: total - completed };
};

const AgentLeadsAdminList = () => {
  const navigate = useNavigate();
  const leadsAllowed = currentUserCanUseLeads();
  const canMutateLeads = canMutateAgentLeads(getUser()?.role);
  const canCreateLeads = canCreateAgentLeads(getUser()?.role);
  const [loading, setLoading] = useState(false);
  const [assignments, setAssignments] = useState<any[]>([]);

  const [fromDate, setFromDate] = useState(FIRST_OF_MONTH_STR);
  const [toDate, setToDate] = useState(TODAY_STR);
  const [search, setSearch] = useState('');

  const [contactGroups, setContactGroups] = useState<any[]>([]);
  const [dispositionForms, setDispositionForms] = useState<any[]>([]);
  const [agents, setAgents] = useState<any[]>([]);
  const [callGroups, setCallGroups] = useState<any[]>([]);
  const [campaigns, setCampaigns] = useState<any[]>([]);

  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [deleting, setDeleting] = useState(false);
  const [completeTarget, setCompleteTarget] = useState<any>(null);
  const [completing, setCompleting] = useState(false);

  const [transferTarget, setTransferTarget] = useState<any>(null);
  const [transferring, setTransferring] = useState(false);
  const [transferForm, setTransferForm] = useState<any>({
    agent_ids: [],
    call_group_ids: [],
  });
  // Recipients of this assignment's call centre (null = loading; falls back to page lists on error).
  const [transferRecipients, setTransferRecipients] = useState<{ agents: any[]; call_groups: any[] } | null>(null);
  // pending | completed | all | specific (picked item ids live in pickedIds)
  const [transferMode, setTransferMode] = useState('pending');
  const [pickedIds, setPickedIds] = useState<Set<number>>(() => new Set());
  const [pickSearch, setPickSearch] = useState('');
  const pickPage = usePagedLeads(
    transferTarget && transferMode === 'specific'
      ? API_AGENT_LEADS_ITEMS.replace(':id', transferTarget.id)
      : null,
    { search: pickSearch },
    25,
  );
  const [editTitleTarget, setEditTitleTarget] = useState<any>(null);
  const [titleTouched, setTitleTouched] = useState(false);
  const [form, setForm] = useState<any>({
    title: '',
    assignment_type: 'agent',
    agent_ids: [],
    call_group_id: '',
    campaign_id: '',
    contact_group_id: '',
    disposition_form_id: '',
  });

  const [leadSource, setLeadSource] = useState<'group' | 'paste'>('group');
  const pasteRef = useRef<HTMLTextAreaElement>(null);
  const [pasteResult, setPasteResult] = useState<LeadPasteResult | null>(null);
  const [saveAsGroup, setSaveAsGroup] = useState(true);
  const [leadBalance, setLeadBalance] = useState<any>(null);

  const fetchLeadBalance = () => {
    getApiCall(API_AGENT_LEADS_BALANCE)
      .then((resp: any) => setLeadBalance(resp || null))
      .catch(() => setLeadBalance(null));
  };

  const verifyPaste = () => {
    const result = parseLeadPaste(pasteRef.current?.value || '');
    setPasteResult(result);
    return result;
  };

  const fetchSeqRef = useRef(0);
  const fetchAssignments = () => {
    const seq = ++fetchSeqRef.current;
    setLoading(true);
    getApiCall(API_AGENT_LEADS_LIST, { from_date: fromDate || undefined, to_date: toDate || undefined })
      .then((resp: any) => {
        if (seq === fetchSeqRef.current) setAssignments(Array.isArray(resp) ? resp : []);
      })
      .catch((err: any) => {
        if (seq === fetchSeqRef.current) toast.error(friendlyApiError(err) || 'Unable to load lead assignments');
      })
      .finally(() => {
        if (seq === fetchSeqRef.current) setLoading(false);
      });
  };

  const fetchDropdownData = () => {
    getApiCall(API_CONTACT_GROUP_LIST)
      .then((resp: any) => setContactGroups(Array.isArray(resp) ? resp : []))
      .catch(() => {});
    getApiCall(API_DISPOSITION_LIST, { submissions: 0 })
      .then((resp: any) => setDispositionForms(Array.isArray(resp) ? resp : []))
      .catch(() => {});
    getApiCall(API_AGENTS_LIST, { fields: 'min' })
      .then((resp: any) => setAgents(Array.isArray(resp) ? resp : []))
      .catch(() => {});
    getApiCall(API_AGENT_GROUPS_LIST, { fields: 'min' })
      .then((resp: any) => setCallGroups(Array.isArray(resp) ? resp : []))
      .catch(() => {});
    getApiCall(API_VOICE_CAMPAIGNS_LIST)
      .then((resp: any) => {
        const list = Array.isArray(resp) ? resp : resp?.rows || resp?.data || [];
        setCampaigns(Array.isArray(list) ? list : []);
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (!leadsAllowed) return;
    fetchDropdownData();
    fetchLeadBalance();
  }, []);

  useEffect(() => {
    if (!leadsAllowed) return;
    fetchAssignments();
  }, [fromDate, toDate]);

  const resetForm = () =>
    setForm({
      title: '',
      assignment_type: 'agent',
      agent_ids: [],
      call_group_id: '',
      campaign_id: '',
      contact_group_id: '',
      disposition_form_id: '',
    });

  const openModal = () => {
    resetForm();
    setTitleTouched(false);
    setLeadSource('group');
    setPasteResult(null);
    setSaveAsGroup(true);
    setShowModal(true);
  };

  const selectedGroup = contactGroups.find((g: any) => String(g.id) === String(form.contact_group_id));
  const requestedLeadCount =
    leadSource === 'paste'
      ? Number(pasteResult?.contacts?.length) || 0
      : Number(selectedGroup?.member_count) || 0;

  const toggleAgent = (id: any) => {
    setForm((prev: any) => ({
      ...prev,
      agent_ids: prev.agent_ids.includes(id)
        ? prev.agent_ids.filter((a: any) => a !== id)
        : [...prev.agent_ids, id],
    }));
  };

  const handleSubmit = () => {
    if (leadTitleError(form.title)) {
      setTitleTouched(true);
      document.getElementById('title')?.focus();
      return toast.error(leadTitleError(form.title));
    }
    let pasted: LeadPasteResult | null = null;
    if (leadSource === 'paste') {
      pasted = verifyPaste();
      if (pasted.contacts.length === 0)
        return toast.error('Add at least one valid 10-digit number');
      if (pasted.contacts.length > MAX_LEAD_CONTACTS)
        return toast.error(`At most ${MAX_LEAD_CONTACTS.toLocaleString('en-IN')} numbers can be assigned at once`);
    } else if (!form.contact_group_id) return toast.error('Select a contact group (leads)');
    else if (selectedGroup && !Number(selectedGroup.member_count))
      return toast.error(`No contacts in: ${selectedGroup.group_name}. Choose another group or add contacts first.`);
    if (!form.disposition_form_id) return toast.error('Select a disposition form');
    if (form.assignment_type === 'agent' && form.agent_ids.length === 0)
      return toast.error('Select at least one agent');
    if (form.assignment_type === 'call_group' && !form.call_group_id)
      return toast.error('Select a call group');
    if (form.assignment_type === 'campaign') {
      if (!form.campaign_id) return toast.error('Select a campaign');
      if (!form.call_group_id) return toast.error('Select a call group');
    }
    const countForCheck = pasted ? pasted.contacts.length : requestedLeadCount;
    if (leadBalance?.limited && countForCheck > Number(leadBalance.remaining)) {
      return toast.error(
        `This call centre has ${Number(leadBalance.remaining).toLocaleString('en-IN')} agent leads left. This assignment needs ${countForCheck.toLocaleString('en-IN')}.`,
      );
    }
    const isCampaign = form.assignment_type === 'campaign';
    const backendType = isCampaign ? 'call_group' : form.assignment_type;

    const payload: any = {
      title: form.title.trim(),
      assignment_type: backendType,
      ...(pasted
        ? { contacts: pasted.contacts, save_as_contact_group: saveAsGroup }
        : { contact_group_id: form.contact_group_id }),
      disposition_form_id: form.disposition_form_id,
      agent_ids: form.assignment_type === 'agent' ? form.agent_ids : undefined,
      call_group_id:
        form.assignment_type === 'call_group' || isCampaign
          ? form.call_group_id
          : undefined,
      campaign_id: isCampaign ? form.campaign_id : undefined,
    };

    setSubmitting(true);
    postApiCall(API_AGENT_LEADS_CREATE, payload)
      .then((resp: any) => {
        const total = Number(resp?.total_leads) || 0;
        toast.success(
          pasted
            ? `${total.toLocaleString('en-IN')} leads assigned` +
                (resp?.contact_group_name ? ` · saved as contact group "${resp.contact_group_name}"` : '')
            : 'Leads assigned successfully',
        );
        setShowModal(false);
        fetchAssignments();
        fetchLeadBalance();
        if (pasted && saveAsGroup) fetchDropdownData();
      })
      .catch((err: any) => toast.error(friendlyApiError(err) || 'Unable to assign leads'))
      .finally(() => setSubmitting(false));
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    setDeleting(true);
    postApiCall(API_AGENT_LEADS_DELETE.replace(':id', deleteTarget), {})
      .then(() => {
        toast.success('Assignment deleted');
        setDeleteTarget(null);
        fetchAssignments();
        fetchLeadBalance();
      })
      .catch((err: any) => toast.error(friendlyApiError(err) || 'Unable to delete'))
      .finally(() => setDeleting(false));
  };

  const confirmComplete = () => {
    if (!completeTarget) return;
    setCompleting(true);
    postApiCall(API_AGENT_LEADS_MARK_COMPLETE.replace(':id', completeTarget.id), {})
      .then(() => {
        toast.success('Assignment marked as completed');
        setCompleteTarget(null);
        fetchAssignments();
      })
      .catch((err: any) => toast.error(friendlyApiError(err) || 'Unable to mark as completed'))
      .finally(() => setCompleting(false));
  };

  const openTransfer = (row: any) => {
    const current = Array.isArray(row.agent_ids)
      ? row.agent_ids.map((id: any) => Number(id))
      : Array.isArray(row.agents)
        ? row.agents.map((a: any) => Number(a.id))
        : [];
    const currentGroups = Array.isArray(row.call_group_ids)
      ? row.call_group_ids.map((id: any) => Number(id))
      : row.call_group_id
        ? [Number(row.call_group_id)]
        : [];
    setTransferForm({ agent_ids: current, call_group_ids: currentGroups });
    setTransferMode(transferCounts(row).pending > 0 ? 'pending' : 'all');
    setPickedIds(new Set());
    setPickSearch('');
    setTransferTarget(row);
    setTransferRecipients(null);
    getApiCall(API_AGENT_LEADS_TRANSFER_RECIPIENTS.replace(':id', row.id))
      .then((resp: any) =>
        setTransferRecipients({
          agents: [...(resp?.agents || [])].sort((a: any, b: any) =>
            (a.agent_name || '').localeCompare(b.agent_name || ''),
          ),
          call_groups: resp?.call_groups || [],
        }),
      )
      .catch(() => setTransferRecipients({ agents: sortedAgents, call_groups: callGroups }));
  };
  const transferAgents = transferRecipients?.agents ?? [];
  const transferGroups = transferRecipients?.call_groups ?? [];

  const tc = transferCounts(transferTarget);
  const transferCount =
    transferMode === 'specific'
      ? pickedIds.size
      : transferMode === 'pending'
        ? tc.pending
        : transferMode === 'completed'
          ? tc.completed
          : tc.total;

  const pickRows = pickPage.rows;
  const pickPageIds = pickRows
    .filter((it: any) => !it.is_deleted)
    .map((it: any) => Number(it.id));
  const pickPagePicked = pickPageIds.filter((itemId: number) => pickedIds.has(itemId)).length;
  const transferSplits = transferMode !== 'all' && transferCount > 0 && transferCount < tc.total;
  const transferTitle =
    transferTarget?.title || transferTarget?.contact_group?.group_name || 'this assignment';
  const leadsWord = (n: number) => `${n.toLocaleString('en-IN')} ${n === 1 ? 'lead' : 'leads'}`;

  const togglePicked = (ids: number[], on: boolean) =>
    setPickedIds((prev) => {
      const next = new Set(prev);
      ids.forEach((itemId) => (on ? next.add(itemId) : next.delete(itemId)));
      return next;
    });

  const toggleTransferAgent = (id: any) => {
    setTransferForm((prev: any) => ({
      ...prev,
      agent_ids: prev.agent_ids.includes(id)
        ? prev.agent_ids.filter((a: any) => a !== id)
        : [...prev.agent_ids, id],
    }));
  };

  const toggleTransferGroup = (id: any) => {
    setTransferForm((prev: any) => ({
      ...prev,
      call_group_ids: prev.call_group_ids.includes(id)
        ? prev.call_group_ids.filter((g: any) => g !== id)
        : [...prev.call_group_ids, id],
    }));
  };

  const confirmTransfer = () => {
    if (!transferTarget) return;
    if (
      transferForm.agent_ids.length === 0 &&
      transferForm.call_group_ids.length === 0
    ) {
      return toast.error('Select at least one agent or call group to transfer to');
    }
    if (transferMode !== 'all' && transferCount === 0) {
      return toast.error('Select at least one lead to transfer');
    }
    setTransferring(true);
    postApiCall(API_AGENT_LEADS_TRANSFER.replace(':id', transferTarget.id), {
      agent_ids: transferForm.agent_ids,
      call_group_ids: transferForm.call_group_ids,
      mode: transferMode,
      ...(transferMode === 'specific' ? { item_ids: [...pickedIds] } : {}),
    })
      .then((resp: any) => {
        toast.success(
          resp?.split
            ? `${Number(resp.moved || 0).toLocaleString('en-IN')} leads moved to a new assignment`
            : 'Leads transferred successfully',
        );
        setTransferTarget(null);
        fetchAssignments();
      })
      .catch((err: any) => toast.error(friendlyApiError(err) || 'Unable to transfer leads'))
      .finally(() => setTransferring(false));
  };

  const groupNameById = useMemo(() => {
    const m: any = {};
    callGroups.forEach((g: any) => {
      m[Number(g.id)] = g.group_name || g.name;
    });
    return m;
  }, [callGroups]);

  const assignmentTitleById = useMemo(() => {
    const m: any = {};
    assignments.forEach((a: any) => {
      m[Number(a.id)] = a.title;
    });
    return m;
  }, [assignments]);

  const rowGroupNames = (row: any) => {
    const ids = Array.isArray(row.call_group_ids)
      ? row.call_group_ids
      : row.call_group_id
        ? [row.call_group_id]
        : [];
    return ids
      .map(
        (id: any) =>
          groupNameById[Number(id)] || row.call_group?.group_name || `#${id}`,
      )
      .filter(Boolean);
  };

  const assignedToLabel = (row: any) => {
    const parts: any[] = [];
    rowGroupNames(row).forEach((n: any) => parts.push(`👥 ${n}`));
    const names = Array.isArray(row.agents)
      ? row.agents.map((a: any) => a.agent_name || a.agent_number || `#${a.id}`)
      : [];
    parts.push(...names);
    if (parts.length) return parts.join(', ');
    const ids = Array.isArray(row.agent_ids) ? row.agent_ids : [];
    return ids.length ? ids.map((id: any) => `#${id}`).join(', ') : '—';
  };

  const transferredByName = (row: any) =>
    row.transferred_by_user?.username || row.transferred_by_username || null;

  const agentNameById = useMemo(() => {
    const m: any = {};
    agents.forEach((a: any) => {
      m[Number(a.id)] = a.agent_name
        ? `${a.agent_name}${a.agent_number ? ` (${a.agent_number})` : ''}`
        : a.agent_number || `#${a.id}`;
    });
    return m;
  }, [agents]);

  const transferredFromLabel = (row: any) => {
    const from = row.transferred_from;
    if (!from) return '—';
    const parts: any[] = [];
    const groupIds = Array.isArray(from.call_group_ids) ? from.call_group_ids : [];
    groupIds.forEach((id: any) =>
      parts.push(`👥 ${groupNameById[Number(id)] || `#${id}`}`),
    );
    const fromAgents = Array.isArray(row.transferred_from_agents)
      ? row.transferred_from_agents
      : [];
    if (fromAgents.length) {
      fromAgents.forEach((a: any) =>
        parts.push(a.agent_name || a.agent_number || `#${a.id}`),
      );
    } else {
      const ids = Array.isArray(from.agent_ids) ? from.agent_ids : [];
      ids.forEach((id: any) =>
        parts.push(agentNameById[Number(id)] || `#${id}`),
      );
    }
    return parts.length ? parts.join(', ') : '—';
  };

  const sortedAgents = useMemo(
    () =>
      [...agents]
        .filter((a: any) => String(a.access).toLowerCase() !== 'team_lead')
        .sort((a: any, b: any) =>
          (a.agent_name || '').localeCompare(b.agent_name || ''),
        ),
    [agents],
  );

  const outboundDispositionForms = useMemo(
    () =>
      dispositionForms.filter((d: any) =>
        formMatchesCallDirection(d.call_direction, 'outbound'),
      ),
    [dispositionForms],
  );

  const selectedCampaign = useMemo(
    () =>
      form.campaign_id
        ? campaigns.find(
            (c: any) => String(c.campaign_id) === String(form.campaign_id),
          ) || null
        : null,
    [form.campaign_id, campaigns],
  );

  const filteredAssignments = useMemo(() => {
    const fromTs = fromDate ? new Date(`${fromDate}T00:00:00.000`).getTime() : null;
    const toTs = toDate ? new Date(`${toDate}T23:59:59.999`).getTime() : null;
    const q = search.trim().toLowerCase();
    return assignments.filter((row: any) => {
      if (!row.created_at) {
        if (fromTs || toTs) return false;
      } else {
        const ts = new Date(row.created_at).getTime();
        if (fromTs && ts < fromTs) return false;
        if (toTs && ts > toTs) return false;
      }
      if (q) {
        const haystack = [
          row.title,
          row.contact_group?.group_name,
          row.disposition_form?.name,
          assignedToLabel(row),
          row.status,
          `${row.completed_leads || 0}/${row.total_leads || 0}`,
          row.created_at ? new Date(row.created_at).toLocaleString() : '',
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignments, fromDate, toDate, search]);

  const handleExport = () => {
    if (!filteredAssignments.length) return toast.info('No assignments to export');
    exportToCSV({
      fileName: 'agent-leads-assignments',
      data: filteredAssignments,
      columns: [
        { header: 'Title', accessor: (r: any) => r.title || '-' },
        {
          header: 'Contact Group',
          accessor: (r: any) => r.contact_group?.group_name || '-',
        },
        {
          header: 'Disposition Form',
          accessor: (r: any) => r.disposition_form?.name || '-',
        },
        { header: 'Assigned To', accessor: (r: any) => assignedToLabel(r) },
        { header: 'Completed', accessor: (r: any) => r.completed_leads || 0 },
        { header: 'Total', accessor: (r: any) => r.total_leads || 0 },
        { header: 'Status', accessor: (r: any) => r.status || '-' },
        {
          header: 'Assigned At',
          accessor: (r: any) =>
            r.created_at ? new Date(r.created_at).toLocaleString() : '-',
        },
        {
          header: 'Transferred From',
          accessor: (r: any) => (r.transferred_at ? transferredFromLabel(r) : '-'),
        },
        {
          header: 'Transferred To',
          accessor: (r: any) => (r.transferred_at ? assignedToLabel(r) : '-'),
        },
        {
          header: 'Transferred Leads',
          accessor: (r: any) => (r.transferred_at ? transferMovedLabel(r.transferred_from) || '-' : '-'),
        },
        {
          header: 'Transferred By',
          accessor: (r: any) =>
            r.transferred_at ? transferredByName(r) || '-' : '-',
        },
        {
          header: 'Transferred At',
          accessor: (r: any) =>
            r.transferred_at ? new Date(r.transferred_at).toLocaleString() : '-',
        },
        { header: 'Completed By', accessor: (r: any) => r.completed_by_user?.username || '-' },
        {
          header: 'Completed At',
          accessor: (r: any) =>
            r.completed_at ? new Date(r.completed_at).toLocaleString() : '-',
        },
      ],
    });
  };

  const clearDateFilter = () => {
    setFromDate(FIRST_OF_MONTH_STR);
    setToDate(TODAY_STR);
  };

  const handleFromChange = (val: string) => {
    if (val && toDate && !isWithinThreeMonths(val, toDate)) {
      toast.warning('Date range cannot exceed 3 months.');
      return;
    }
    setFromDate(val);
  };

  const handleToChange = (val: string) => {
    if (val && fromDate && !isWithinThreeMonths(fromDate, val)) {
      toast.warning('Date range cannot exceed 3 months.');
      return;
    }
    setToDate(val);
  };

  const stats = useMemo(() => {
    const totalAssignments = filteredAssignments.length;
    const totalLeads = filteredAssignments.reduce(
      (s: number, r: any) => s + (Number(r.total_leads) || 0),
      0,
    );
    const completed = filteredAssignments.reduce(
      (s: number, r: any) => s + (Number(r.completed_leads) || 0),
      0,
    );
    const pending = Math.max(totalLeads - completed, 0);
    const completionRate = totalLeads
      ? Math.round((completed / totalLeads) * 100)
      : 0;
    return { totalAssignments, totalLeads, completed, pending, completionRate };
  }, [filteredAssignments]);

  const donutOptions: any = {
    chart: { type: 'donut' },
    labels: ['Completed', 'Pending'],
    colors: ['#10b981', '#f59e0b'],
    legend: { position: 'bottom' },
    dataLabels: { enabled: true },
    plotOptions: {
      pie: {
        donut: {
          labels: {
            show: true,
            total: {
              show: true,
              label: 'Total',
              formatter: () => String(stats.totalLeads),
            },
          },
        },
      },
    },
    noData: { text: 'No leads' },
  };
  const donutSeries = [stats.completed, stats.pending];

  const barCategories = filteredAssignments.map(
    (r: any) => r.title || r.contact_group?.group_name || `#${r.id}`,
  );
  const barOptions: any = {
    chart: { type: 'bar', stacked: true, toolbar: { show: false } },
    plotOptions: {
      bar: { horizontal: false, borderRadius: 4, columnWidth: '45%' },
    },
    colors: ['#10b981', '#f59e0b'],
    xaxis: { categories: barCategories },
    legend: { position: 'bottom' },
    dataLabels: { enabled: false },
    noData: { text: 'No leads' },
  };
  const barSeries = [
    {
      name: 'Completed',
      data: filteredAssignments.map((r: any) => Number(r.completed_leads) || 0),
    },
    {
      name: 'Pending',
      data: filteredAssignments.map((r: any) =>
        Math.max(
          (Number(r.total_leads) || 0) - (Number(r.completed_leads) || 0),
          0,
        ),
      ),
    },
  ];

  if (!leadsAllowed) {
    return <AclDenied operation="leads" />;
  }

  return (
    <ScrollPage className="max-w-none">
      <PageHeader
        title="Agent Leads"
        description="Assign contact-group numbers as leads to agents, call groups."
        actions={
          <>
            <Button
              variant="outline"
              size="icon"
              onClick={() => { fetchAssignments(); fetchLeadBalance(); }}
              disabled={loading}
              title="Refresh"
              aria-label="Refresh"
            >
              <RefreshCw className={`h-4 w-4${loading ? ' animate-spin' : ''}`} />
            </Button>
            <Button
              variant="outline"
              onClick={handleExport}
              disabled={loading || filteredAssignments.length === 0}
            >
              <Download className="h-4 w-4" />
              Export
            </Button>
            {canCreateLeads ? (
              <Button onClick={openModal}>
                <Plus className="h-4 w-4" />
                Assign Leads
              </Button>
            ) : null}
          </>
        }
      />

      {/* Stats */}
      {!loading && (
        <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
          {[
            { label: 'Assignments', value: stats.totalAssignments, tone: 'text-foreground' },
            { label: 'Total Leads', value: stats.totalLeads, tone: 'text-foreground' },
            { label: 'Completed', value: stats.completed, tone: 'text-emerald-600' },
            { label: 'Pending', value: stats.pending, tone: 'text-amber-600' },
          ].map((s) => (
            <Card key={s.label}>
              <CardContent className="py-4">
                <div className="text-xs text-muted-foreground">{s.label}</div>
                <div className={cn('mt-1 text-2xl font-bold', s.tone)}>{s.value}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Charts */}
      {!loading && filteredAssignments.length > 0 && (
        <div className="mb-4 grid grid-cols-1 gap-3 lg:grid-cols-3">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">
                Completed vs Pending ({stats.completionRate}%)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Chart options={donutOptions} series={donutSeries} type="donut" height={260} />
            </CardContent>
          </Card>
          <Card className="lg:col-span-2">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Leads by Assignment</CardTitle>
            </CardHeader>
            <CardContent>
              <Chart options={barOptions} series={barSeries} type="bar" height={260} />
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="mb-4 rounded-md border border-border bg-card p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_160px_160px_auto] md:items-end">
          <div className="space-y-1.5">
            <Label htmlFor="search" className="text-xs">
              Search
            </Label>
            <Input
              id="search"
              placeholder="Search assignments…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="from" className="text-xs">
              From
            </Label>
            <CalendarDateInput
              id="from"
              value={fromDate}
              onChange={(e) => handleFromChange(e)}
              min={MIN_DATE_STR}
              max={toDate || TODAY_STR}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="to" className="text-xs">
              To
            </Label>
            <CalendarDateInput
              id="to"
              value={toDate}
              onChange={(e) => handleToChange(e)}
              min={fromDate || MIN_DATE_STR}
              max={TODAY_STR}
            />
          </div>
          <div>
            <Button variant="outline" onClick={clearDateFilter}>
              <RotateCcw className="h-4 w-4" />
              Reset
            </Button>
          </div>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <Loader />
      ) : (
        <div className="overflow-hidden rounded-md border border-border">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr>
                  <th className="w-10 px-3 py-2 text-left font-medium">#</th>
                  <th className="px-3 py-2 text-left font-medium">Title</th>
                  <th className="px-3 py-2 text-left font-medium">Contact Group</th>
                  {/* <th className="px-3 py-2 text-left font-medium">Campaign</th> */}
                  <th className="px-3 py-2 text-left font-medium">Disposition Form</th>
                  <th className="px-3 py-2 text-left font-medium">Assigned To</th>
                  <th className="px-3 py-2 text-left font-medium">Progress</th>
                  <th className="px-3 py-2 text-left font-medium">Status</th>
                  <th className="px-3 py-2 text-left font-medium">Assigned At</th>
                  <th className="w-16 px-3 py-2 text-right" />
                </tr>
              </thead>
              <tbody>
                {filteredAssignments.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="px-3 py-8 text-center text-muted-foreground">
                      {search.trim()
                        ? 'No assignments match your search.'
                        : fromDate || toDate
                          ? 'No lead assignments in selected date range.'
                          : 'No lead assignments yet.'}
                    </td>
                  </tr>
                ) : (
                  filteredAssignments.map((row: any, idx: number) => (
                    <tr key={row.id} className="border-t border-border">
                      <td className="px-3 py-2 text-xs text-muted-foreground">{idx + 1}</td>
                      <td className="px-3 py-2 font-medium">
                        <Link
                          to={`/${ROUTES.AGENT_LEADS.ADMIN_LIST}/${row.id}`}
                          className="hover:underline"
                        >
                          {row.title || '—'}
                        </Link>
                      </td>
                      <td className="px-3 py-2">
                        {row.contact_group?.group_name || '—'}
                      </td>
                      {/* <td className="px-3 py-2">
                        {row.campaign ? (
                          <>
                            <div>{row.campaign.campaign_name}</div>
                            <div className="text-xs text-muted-foreground">
                              #{row.campaign.campaign_id}
                              {row.campaign.campaign_type
                                ? ` · ${
                                    CAMPAIGN_TYPE_LABEL[row.campaign.campaign_type] ||
                                    row.campaign.campaign_type
                                  }`
                                : ''}
                            </div>
                          </>
                        ) : (
                          '—'
                        )}
                      </td> */}
                      <td className="px-3 py-2">
                        {row.disposition_form?.name || '—'}
                      </td>
                      <td className="px-3 py-2">
                        <div className="whitespace-normal">{assignedToLabel(row)}</div>
                        {row.transferred_at && (
                          <div className="mt-1 text-xs text-sky-700 dark:text-sky-400">
                            🔁 Transferred{' '}
                            {new Date(row.transferred_at).toLocaleString()}
                            {transferredByName(row) ? ` by ${transferredByName(row)}` : ''}
                            {transferredFromLabel(row) !== '—' && (
                              <div className="text-muted-foreground">
                                from:{' '}
                                <span className="font-medium text-foreground">
                                  {transferredFromLabel(row)}
                                </span>
                              </div>
                            )}
                            <div className="text-muted-foreground">
                              → to: {assignedToLabel(row)}
                            </div>
                            {transferMovedLabel(row.transferred_from) && (
                              <div className="text-muted-foreground">
                                moved: {transferMovedLabel(row.transferred_from)}
                                {row.transferred_from?.assignment_id ? (
                                  <>
                                    {' · split from '}
                                    <Link
                                      to={`/${ROUTES.AGENT_LEADS.ADMIN_LIST}/${row.transferred_from.assignment_id}`}
                                      className="font-medium text-foreground hover:underline"
                                    >
                                      {assignmentTitleById[Number(row.transferred_from.assignment_id)] ||
                                        `#${row.transferred_from.assignment_id}`}
                                    </Link>
                                  </>
                                ) : null}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">
                        {row.completed_leads || 0}/{row.total_leads || 0}
                      </td>
                      <td className="px-3 py-2">
                        <StatusBadge status={row.status} />
                        {row.completed_at && (
                          <div className="mt-1 text-xs text-muted-foreground">
                            {new Date(row.completed_at).toLocaleString()}
                            {row.completed_by_user?.username ? ` by ${row.completed_by_user.username}` : ''}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-muted-foreground">
                        {row.created_at
                          ? new Date(row.created_at).toLocaleString()
                          : '—'}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              aria-label="Row actions"
                            >
                              <Settings2 className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuLabel className="truncate">
                              {row.title || `Assignment #${row.id}`}
                            </DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() =>
                                navigate(
                                  `/${ROUTES.AGENT_LEADS.ADMIN_LIST}/${row.id}`,
                                )
                              }
                            >
                              <Eye className="h-4 w-4" />
                              View
                            </DropdownMenuItem>
                            {canMutateLeads ? (
                              <DropdownMenuItem onClick={() => setEditTitleTarget(row)}>
                                <Pencil className="h-4 w-4" />
                                Edit title
                              </DropdownMenuItem>
                            ) : null}
                            {canMutateLeads ? (
                              <DropdownMenuItem
                                onClick={() => openTransfer(row)}
                                disabled={!Number(row.total_leads)}
                              >
                                <ArrowLeftRight className="h-4 w-4" />
                                Transfer
                              </DropdownMenuItem>
                            ) : null}
                            {canMutateLeads && row.status !== 'completed' ? (
                              <DropdownMenuItem onClick={() => setCompleteTarget(row)}>
                                <CheckCheck className="h-4 w-4" />
                                Mark as completed
                              </DropdownMenuItem>
                            ) : null}
                            {canMutateLeads ? (
                              <DropdownMenuItem
                                onClick={() => setDeleteTarget(row.id)}
                                className="text-destructive focus:text-destructive"
                              >
                                <Trash2 className="h-4 w-4" />
                                Delete
                              </DropdownMenuItem>
                            ) : null}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Assign leads modal */}
      <Dialog open={showModal} onOpenChange={(o) => !o && setShowModal(false)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Assign Leads</DialogTitle>
          </DialogHeader>
          {leadBalance?.limited ? (
            <p
              className={`text-xs ${
                requestedLeadCount > Number(leadBalance.remaining) ? 'text-destructive' : 'text-muted-foreground'
              }`}
            >
              {`${Number(leadBalance.remaining).toLocaleString('en-IN')} of ${Number(leadBalance.allowance).toLocaleString('en-IN')} leads left`}
              {Number(leadBalance.associated) > 0
                ? ` · ${Number(leadBalance.associated).toLocaleString('en-IN')} already assigned`
                : ''}
              {requestedLeadCount > 0
                ? ` · this assignment uses ${requestedLeadCount.toLocaleString('en-IN')}`
                : ''}
            </p>
          ) : null}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">
                Title <span className="text-destructive">*</span>
              </Label>
              <Input
                id="title"
                type="text"
                placeholder="e.g. October renewals"
                value={form.title}
                maxLength={LEAD_TITLE_MAX}
                aria-invalid={titleTouched && !!leadTitleError(form.title)}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                onBlur={() => setTitleTouched(true)}
              />
              {titleTouched && leadTitleError(form.title) ? (
                <p className="text-xs text-destructive">{leadTitleError(form.title)}</p>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label>
                Leads <span className="text-destructive">*</span>
              </Label>
              <div className="inline-flex rounded-md border p-0.5">
                {[
                  { key: 'group', label: 'Pick contact group' },
                  { key: 'paste', label: 'Paste numbers' },
                ].map((opt) => (
                  <Button
                    key={opt.key}
                    type="button"
                    size="sm"
                    variant={leadSource === opt.key ? 'default' : 'ghost'}
                    onClick={() => setLeadSource(opt.key as 'group' | 'paste')}
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
            </div>

            {leadSource === 'paste' && (
              <div className="space-y-2">
                <Label htmlFor="lead-paste">
                  Numbers — one per line: number, First Name, Last Name
                </Label>
                <Textarea
                  id="lead-paste"
                  ref={pasteRef}
                  rows={8}
                  spellCheck={false}
                  className="font-mono text-xs"
                  placeholder={'9876543210, Ravi, Kumar\n9123456789\n+91 98111 22333, Priya'}
                  onChange={() => pasteResult && setPasteResult(null)}
                  onPaste={() => setTimeout(verifyPaste, 0)}
                />
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-xs text-muted-foreground">
                    Only the 10-digit number is required (+91 / 0 prefix is fine). Missing first
                    name is saved as "Unknown". Up to {MAX_LEAD_CONTACTS.toLocaleString('en-IN')} numbers.
                  </p>
                  <Button type="button" size="sm" variant="outline" onClick={verifyPaste}>
                    Verify
                  </Button>
                </div>
                {pasteResult && (
                  <div
                    className={cn(
                      'rounded-md border px-3 py-2 text-xs',
                      pasteResult.contacts.length === 0 ||
                        pasteResult.contacts.length > MAX_LEAD_CONTACTS
                        ? 'border-destructive/40 bg-destructive/5 text-destructive'
                        : 'bg-muted/40',
                    )}
                  >
                    <span className="font-medium">
                      {pasteResult.contacts.length.toLocaleString('en-IN')} valid
                    </span>
                    {pasteResult.duplicates > 0 &&
                      ` · ${pasteResult.duplicates.toLocaleString('en-IN')} duplicates removed`}
                    {pasteResult.invalidLines.length > 0 && (
                      <>
                        {` · ${pasteResult.invalidLines.length.toLocaleString('en-IN')} invalid (line `}
                        {pasteResult.invalidLines.slice(0, 10).join(', ')}
                        {pasteResult.invalidLines.length > 10 ? ', …' : ''})
                      </>
                    )}
                    {pasteResult.contacts.length > MAX_LEAD_CONTACTS &&
                      ` · limit is ${MAX_LEAD_CONTACTS.toLocaleString('en-IN')}`}
                  </div>
                )}
                <label className="flex cursor-pointer items-start gap-2 text-sm">
                  <Checkbox
                    checked={saveAsGroup}
                    onCheckedChange={(v) => setSaveAsGroup(v === true)}
                    className="mt-0.5"
                  />
                  <span>
                    Save as contact group
                    <span className="block text-xs text-muted-foreground">
                      Creates "Agent Leads · date · N Contacts" so these numbers can be reused later.
                    </span>
                  </span>
                </label>
              </div>
            )}

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {leadSource === 'group' && (
              <div className="space-y-2">
                <Label htmlFor="cg">
                  Contact Group (leads) <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={form.contact_group_id ? String(form.contact_group_id) : ''}
                  onValueChange={(v) => setForm({ ...form, contact_group_id: v })}
                >
                  <SelectTrigger id="cg">
                    <SelectValue placeholder="Select contact group" />
                  </SelectTrigger>
                  <SelectContent>
                    {contactGroups.map((g: any) => (
                      <SelectItem key={g.id} value={String(g.id)} disabled={!Number(g.member_count)}>
                        {g.group_name}
                        {Number(g.member_count) ? ` (${g.member_count})` : ' (No contacts)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="df">
                  Disposition Form (Outbound or Both){' '}
                  <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={form.disposition_form_id ? String(form.disposition_form_id) : ''}
                  onValueChange={(v) => setForm({ ...form, disposition_form_id: v })}
                >
                  <SelectTrigger id="df">
                    <SelectValue placeholder="Select disposition form" />
                  </SelectTrigger>
                  <SelectContent>
                    {outboundDispositionForms.map((d: any) => (
                      <SelectItem key={d.id} value={String(d.id)}>
                        {d.name} ({formatDispositionCallDirectionLabel(d.call_direction)})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>
                Assign To <span className="text-destructive">*</span>
              </Label>
              <div className="flex flex-wrap gap-4">
                {[
                  { value: 'agent', label: 'Specific Agents' },
                  { value: 'call_group', label: 'Call Group' },
                  // { value: 'campaign', label: 'Campaign' },
                ].map((opt) => (
                  <label
                    key={opt.value}
                    className="inline-flex cursor-pointer items-center gap-2 text-sm"
                  >
                    <input
                      type="radio"
                      name="assignment_type"
                      className="h-4 w-4 accent-primary"
                      checked={form.assignment_type === opt.value}
                      onChange={() => setForm({ ...form, assignment_type: opt.value })}
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>

            {form.assignment_type === 'agent' && (
              <div className="space-y-2">
                <Label>Select Agents</Label>
                <div className="max-h-52 overflow-y-auto rounded-md border border-border p-2">
                  {sortedAgents.length === 0 ? (
                    <div className="text-sm text-muted-foreground">No agents found</div>
                  ) : (
                    sortedAgents.map((a: any) => (
                      <label
                        key={a.id}
                        className="flex cursor-pointer items-center gap-2 py-0.5 text-sm"
                      >
                        <Checkbox
                          checked={form.agent_ids.includes(a.id)}
                          onCheckedChange={() => toggleAgent(a.id)}
                        />
                        <span>
                          {a.agent_name} ({a.agent_number})
                        </span>
                      </label>
                    ))
                  )}
                </div>
              </div>
            )}

            {form.assignment_type === 'call_group' && (
              <div className="space-y-2">
                <Label>Select Call Group</Label>
                <Select
                  value={form.call_group_id ? String(form.call_group_id) : ''}
                  onValueChange={(v) => setForm({ ...form, call_group_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select call group" />
                  </SelectTrigger>
                  <SelectContent>
                    {callGroups.map((g: any) => (
                      <SelectItem key={g.id} value={String(g.id)}>
                        {g.group_name || g.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  All agents currently in this call group will receive these leads.
                </p>
              </div>
            )}

            {form.assignment_type === 'campaign' && (
              <>
                <div className="space-y-2">
                  <Label>
                    Select Campaign <span className="text-destructive">*</span>
                  </Label>
                  <Select
                    value={form.campaign_id ? String(form.campaign_id) : ''}
                    onValueChange={(v) => setForm({ ...form, campaign_id: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select campaign" />
                    </SelectTrigger>
                    <SelectContent>
                      {campaigns.map((c: any) => (
                        <SelectItem key={c.campaign_id} value={String(c.campaign_id)}>
                          {c.campaign_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedCampaign && (
                    <div className="rounded-md border border-border bg-muted/40 p-2 text-xs">
                      <div>
                        <strong>Campaign:</strong> {selectedCampaign.campaign_name}{' '}
                        <span className="text-muted-foreground">
                          #{selectedCampaign.campaign_id}
                        </span>
                      </div>
                      <div>
                        <strong>Type:</strong>{' '}
                        {CAMPAIGN_TYPE_LABEL[selectedCampaign.campaign_type] ||
                          selectedCampaign.campaign_type ||
                          '—'}
                        {' · '}
                        <strong>Status:</strong>{' '}
                        {selectedCampaign.campaign_status ?? '—'}
                      </div>
                      {(selectedCampaign.start_date || selectedCampaign.end_date) && (
                        <div className="text-muted-foreground">
                          {selectedCampaign.start_date || '?'} →{' '}
                          {selectedCampaign.end_date || '?'}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>
                    Select Call Group <span className="text-destructive">*</span>
                  </Label>
                  <Select
                    value={form.call_group_id ? String(form.call_group_id) : ''}
                    onValueChange={(v) => setForm({ ...form, call_group_id: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select call group" />
                    </SelectTrigger>
                    <SelectContent>
                      {callGroups.map((g: any) => (
                        <SelectItem key={g.id} value={String(g.id)}>
                          {g.group_name || g.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Leads go to all agents currently in this call group, linked to the
                    selected campaign.
                  </p>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowModal(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {submitting ? 'Assigning…' : 'Assign'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer modal */}
      <Dialog
        open={transferTarget !== null}
        onOpenChange={(o) => !o && !transferring && setTransferTarget(null)}
      >
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowLeftRight className="h-5 w-5 text-primary" />
              Transfer Leads
            </DialogTitle>
            {transferTarget && (
              <DialogDescription asChild>
                <div className="mt-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-800 dark:text-amber-300">
                  {transferMode === 'all' ? (
                    <>
                      <strong>Move / Replace:</strong> the agents and call groups you pick
                      below become the <strong>only</strong> recipients of all{' '}
                      <strong>{leadsWord(tc.total)}</strong> in <strong>{transferTitle}</strong>.
                      Completed leads keep their status and submissions.
                    </>
                  ) : (
                    <>
                      Moves <strong>{leadsWord(transferCount)}</strong>
                      {transferMode === 'pending'
                        ? ' (pending)'
                        : transferMode === 'completed'
                          ? ' (completed, with their submissions)'
                          : ' you pick below'}{' '}
                      from <strong>{transferTitle}</strong> to the recipients below.{' '}
                      {transferSplits
                        ? `They go to a new assignment with the same title; the other ${leadsWord(
                            tc.total - transferCount,
                          )} stay with the current agents.`
                        : transferCount > 0
                          ? 'That is every lead, so the whole assignment moves.'
                          : ''}
                    </>
                  )}
                </div>
              </DialogDescription>
            )}
          </DialogHeader>

          <div className="space-y-2">
            <Label>Leads to transfer</Label>
            <div className="inline-flex flex-wrap rounded-md border p-0.5">
              {[
                { key: 'pending', label: 'Pending', count: tc.pending },
                { key: 'completed', label: 'Completed', count: tc.completed },
                { key: 'all', label: 'All', count: tc.total },
                { key: 'specific', label: 'Pick leads', count: pickedIds.size },
              ].map((opt) => (
                <Button
                  key={opt.key}
                  type="button"
                  size="sm"
                  variant={transferMode === opt.key ? 'default' : 'ghost'}
                  aria-pressed={transferMode === opt.key}
                  disabled={
                    transferring ||
                    ((opt.key === 'pending' || opt.key === 'completed') && opt.count === 0)
                  }
                  onClick={() => setTransferMode(opt.key)}
                >
                  {opt.label}
                  <span className="tabular-nums opacity-70">
                    {opt.count.toLocaleString('en-IN')}
                  </span>
                </Button>
              ))}
            </div>
          </div>

          {transferMode === 'specific' && (
            <div className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Input
                  className="max-w-xs"
                  placeholder="Search name or number"
                  value={pickSearch}
                  onChange={(e) => setPickSearch(e.target.value)}
                />
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  {pickPage.loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  <span>{pickedIds.size.toLocaleString('en-IN')} selected</span>
                  {pickedIds.size > 0 && (
                    <Button type="button" size="sm" variant="ghost" onClick={() => setPickedIds(new Set())}>
                      Clear
                    </Button>
                  )}
                </div>
              </div>
              <div className="overflow-hidden rounded-md border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40">
                    <tr>
                      <th className="w-10 px-3 py-2 text-left">
                        <Checkbox
                          aria-label="Select all leads on this page"
                          disabled={pickPageIds.length === 0}
                          checked={
                            pickPageIds.length > 0 && pickPagePicked === pickPageIds.length
                              ? true
                              : pickPagePicked > 0
                                ? 'indeterminate'
                                : false
                          }
                          onCheckedChange={() =>
                            togglePicked(pickPageIds, pickPagePicked !== pickPageIds.length)
                          }
                        />
                      </th>
                      <th className="px-3 py-2 text-left font-medium">Name</th>
                      <th className="px-3 py-2 text-left font-medium">Number</th>
                      <th className="px-3 py-2 text-left font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pickRows.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-3 py-6 text-center text-muted-foreground">
                          {pickPage.loading
                            ? 'Loading…'
                            : pickSearch
                              ? 'No leads match your search.'
                              : 'No leads in this assignment.'}
                        </td>
                      </tr>
                    ) : (
                      pickRows.map((it: any) => {
                        const itemId = Number(it.id);
                        const deleted = !!it.is_deleted;
                        const picked = pickedIds.has(itemId);
                        return (
                          <tr
                            key={it.id}
                            className={cn(
                              'border-t border-border',
                              deleted ? 'opacity-50' : 'cursor-pointer hover:bg-muted/30',
                            )}
                            onClick={() => !deleted && togglePicked([itemId], !picked)}
                          >
                            <td className="px-3 py-1.5">
                              <Checkbox
                                aria-label={`Select ${it.contact_name || it.contact_number}`}
                                checked={picked}
                                disabled={deleted}
                                onClick={(e) => e.stopPropagation()}
                                onCheckedChange={(v) => togglePicked([itemId], v === true)}
                              />
                            </td>
                            <td className="px-3 py-1.5">{it.contact_name || '—'}</td>
                            <td className="px-3 py-1.5 font-mono text-xs">{it.contact_number}</td>
                            <td className="px-3 py-1.5">
                              {deleted ? (
                                <span className="text-xs text-muted-foreground">Deleted</span>
                              ) : (
                                <StatusBadge status={it.status} />
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
                <LeadsPager
                  page={pickPage.page}
                  total={pickPage.total}
                  limit={pickPage.limit}
                  onPage={pickPage.setPage}
                />
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Agents</Label>
              <div className="max-h-60 overflow-y-auto rounded-md border border-border p-2">
                {!transferRecipients ? (
                  <div className="text-sm text-muted-foreground">Loading…</div>
                ) : transferAgents.length === 0 ? (
                  <div className="text-sm text-muted-foreground">No agents found</div>
                ) : (
                  transferAgents.map((a: any) => (
                    <label
                      key={a.id}
                      className="flex cursor-pointer items-center gap-2 py-0.5 text-sm"
                    >
                      <Checkbox
                        checked={transferForm.agent_ids.includes(a.id)}
                        onCheckedChange={() => toggleTransferAgent(a.id)}
                      />
                      <span>
                        {a.agent_name} ({a.agent_number})
                      </span>
                    </label>
                  ))
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Call Groups</Label>
              <div className="max-h-60 overflow-y-auto rounded-md border border-border p-2">
                {!transferRecipients ? (
                  <div className="text-sm text-muted-foreground">Loading…</div>
                ) : transferGroups.length === 0 ? (
                  <div className="text-sm text-muted-foreground">
                    No call groups found
                  </div>
                ) : (
                  transferGroups.map((g: any) => (
                    <label
                      key={g.id}
                      className="flex cursor-pointer items-center gap-2 py-0.5 text-sm"
                    >
                      <Checkbox
                        checked={transferForm.call_group_ids.includes(g.id)}
                        onCheckedChange={() => toggleTransferGroup(g.id)}
                      />
                      <span>{g.group_name || g.name}</span>
                    </label>
                  ))
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                All agents currently in a selected call group become recipients.
                Team-lead agents are skipped automatically.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setTransferTarget(null)}
              disabled={transferring}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmTransfer}
              disabled={transferring || (transferMode !== 'all' && transferCount === 0)}
            >
              {transferring ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ArrowRight className="h-4 w-4" />
              )}
              {transferring
                ? 'Transferring…'
                : transferMode === 'all'
                  ? `Transfer all ${leadsWord(tc.total)}`
                  : `Transfer ${leadsWord(transferCount)}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EditLeadTitleDialog
        assignment={editTitleTarget}
        onClose={() => setEditTitleTarget(null)}
        onSaved={(title) => {
          const editedId = editTitleTarget?.id;
          setAssignments((prev) =>
            prev.map((a: any) => (a.id === editedId ? { ...a, title } : a)),
          );
        }}
      />

      {/* Delete confirm */}
      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(o) => !o && !deleting && setDeleteTarget(null)}
        title="Delete Lead Assignment"
        description="Delete this lead assignment? This cannot be undone."
        confirmLabel="Delete"
        tone="destructive"
        loading={deleting}
        onConfirm={confirmDelete}
      />
      <ConfirmDialog
        open={completeTarget !== null}
        onOpenChange={(o) => !o && !completing && setCompleteTarget(null)}
        title="Mark Assignment as Completed"
        description={`Mark "${completeTarget?.title || `Assignment #${completeTarget?.id}`}" as completed? Its ${Math.max(0, (completeTarget?.total_leads || 0) - (completeTarget?.completed_leads || 0)).toLocaleString('en-IN')} pending lead(s) will be closed and agents will no longer see them as pending.`}
        confirmLabel="Mark as completed"
        loading={completing}
        onConfirm={confirmComplete}
      />
    </ScrollPage>
  );
};

export default AgentLeadsAdminList;
